import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

interface ApiKey {
  key: string;
  tenantId: string;
  label: string;
  systemId: string;
  active: boolean;
  created: string;
}

function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTenant, setNewTenant] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newSystem, setNewSystem] = useState('pulsedial');
  const [adding, setAdding] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const loadKeys = () => {
    setLoading(true);
    api.listApiKeys()
      .then((data: any) => setKeys(data.keys || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadKeys(); }, []);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 8000);
  };

  const maskKey = (key: string) => {
    if (key.length <= 12) return key;
    return key.slice(0, 8) + '*'.repeat(key.length - 12) + key.slice(-4);
  };

  const toggleReveal = (key: string) => {
    setRevealedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newTenant.trim()) {
      showMessage('Please enter a tenant ID', 'error');
      return;
    }
    setAdding(true);
    try {
      const result = await api.createApiKey(newTenant.trim(), newLabel.trim() || newTenant.trim(), newSystem);
      showMessage(`API key created: ${result.key}`, 'success');
      setRevealedKeys(prev => new Set(prev).add(result.key));
      setNewTenant('');
      setNewLabel('');
      loadKeys();
    } catch (err: any) {
      showMessage(err.message || 'Failed to create key', 'error');
    }
    setAdding(false);
  };

  const handleDelete = async (key: ApiKey) => {
    if (!window.confirm(`Delete API key for ${key.label}? Any integration using this key will stop working.`)) return;
    try {
      await api.deleteApiKey(key.key, key.tenantId);
      showMessage('API key deleted', 'success');
      loadKeys();
    } catch (err: any) {
      showMessage(err.message || 'Failed to delete key', 'error');
    }
  };

  const handleRegenerate = async (key: ApiKey) => {
    if (!window.confirm(`Regenerate API key for ${key.label}? The old key will stop working immediately.`)) return;
    try {
      const result = await api.regenerateApiKey(key.key, key.tenantId);
      showMessage(`New key: ${result.key}`, 'success');
      setRevealedKeys(prev => new Set(prev).add(result.key));
      loadKeys();
    } catch (err: any) {
      showMessage(err.message || 'Failed to regenerate key', 'error');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => showMessage('Copied to clipboard', 'success'));
  };

  return (
    <div>
      <div className="page-header">
        <h1>API Keys</h1>
        <p>Manage external API keys for system integrations</p>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '6px',
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828',
          border: `1px solid ${message.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
        }}>
          {message.text}
        </div>
      )}

      <div style={{
        background: '#fff',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Create API Key</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newTenant}
            onChange={e => setNewTenant(e.target.value)}
            placeholder="Tenant ID (e.g. pulsedial)"
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            style={{ flex: 1, minWidth: '150px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
          />
          <input
            type="text"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            placeholder="Label (optional)"
            style={{ flex: 1, minWidth: '150px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
          />
          <select
            value={newSystem}
            onChange={e => setNewSystem(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
          >
            <option value="pulsedial">PulseDial</option>
            <option value="vicidial">Vicidial</option>
            <option value="dialedin">DialedIn</option>
          </select>
          <button
            type="button"
            onClick={handleCreate}
            disabled={adding}
            style={{
              padding: '8px 20px', borderRadius: '6px', border: 'none',
              background: adding ? '#999' : '#00bcd4', color: '#fff', fontSize: '14px', cursor: adding ? 'wait' : 'pointer',
            }}
          >
            {adding ? 'Creating...' : 'Create Key'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : keys.length === 0 ? (
        <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
          <p>No API keys created yet.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>API Key</th>
              <th>System</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.key}>
                <td>{k.label}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {revealedKeys.has(k.key) ? k.key : maskKey(k.key)}
                  <button
                    type="button"
                    onClick={() => toggleReveal(k.key)}
                    style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '11px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '3px', background: '#fff' }}
                  >
                    {revealedKeys.has(k.key) ? 'Hide' : 'Show'}
                  </button>
                  {revealedKeys.has(k.key) && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(k.key)}
                      style={{ marginLeft: '4px', padding: '2px 6px', fontSize: '11px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '3px', background: '#fff' }}
                    >
                      Copy
                    </button>
                  )}
                </td>
                <td>
                  <span className={`system-tag ${k.systemId}`}>{k.systemId}</span>
                </td>
                <td>{k.created ? new Date(k.created).toLocaleDateString() : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleRegenerate(k)}
                      style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #80deea', background: '#e0f7fa', color: '#0097a7', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Regenerate
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(k)}
                      style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #ef9a9a', background: '#fff', color: '#c62828', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="setup-card" style={{ marginTop: '24px', background: '#f8f9fa', padding: '16px 20px' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
          For integration documentation (ingest pipeline, storage lifecycle, API endpoints, cost estimates), see the <strong>Integrations</strong> tab on the <strong>Systems</strong> page.
        </p>
      </div>
    </div>
  );
}

export default ApiKeys;
